import { useState, useEffect, useRef } from 'react';
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Button,
	Divider,
	Alert,
} from '@mui/material';

import { authedApiFetch } from 'utils/Api';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';

import { useEmailSyncConfig } from '../hooks/useEmailSyncConfig';
import { useEmailRules } from '../hooks/useEmailRules';
import AutomaticEmailSync from './AutomaticEmailSync';
import EmailRules from './EmailRules';
import BasicSettings from './BasicSettings';
import DynamicDomainList from './DynamicDomainList';

let initialModalState = {};
let initialModalStateString = '';

const UserEmailSyncConfigModal = ({
	tenant,
	user,
	microsoftSyncStartMin,
	googleSyncStartMin,
	isMostRecentJobBlocking,
	open,
	onClose,
	showNotification,
	availableProviders = [],
	onConfigChange,
}) => {
	const [inclusionEnabled, setInclusionEnabled] = useState(
		tenant.use_inclusion_email_rules
	);

	const [syncAttachments, setSyncAttachments] = useState(
		tenant.sync_attachments
	);

	// Available providers
	const hasGoogleProvider = availableProviders.includes('google');
	const hasMicrosoftProvider = availableProviders.includes('microsoft');

	const {
		fetchEmailSyncConfigurations,
		ensureMicrosoftConfigExists,
		fetchMicrosoftFolders,
		ensureGmailConfigExists,
		fetchGmailLabels,
	} = useEmailSyncConfig(
		false, // tenantLevel
		tenant,
		user,
		showNotification,
		onConfigChange
	);

	const { fetchEmailRules, fetchInitialEmailRules } = useEmailRules(
		false, // tenantLevel
		tenant,
		inclusionEnabled,
		open,
		showNotification
	);

	const mapEmailRuleToState = (emailRule) => {
		return {
			ruleType: emailRule.rule_type,
			criteriaType: emailRule.criteria_type,
			value: emailRule.value,
		};
	};

	const stringSort = (a, b) => {
		const nameA = a.toUpperCase();
		const nameB = b.toUpperCase();
		if (nameA < nameB) {
			return -1;
		}
		if (nameA > nameB) {
			return 1;
		}
		return 0;
	};

	const sortSimplifyStringifyModalState = (modalState) => {
		return JSON.stringify({
			gmail: modalState.gmail,
			microsoft: modalState.microsoft,
			includeEmailBody: modalState.includeEmailBody,
			syncAttachments: modalState.syncAttachments,
			emailDirection: modalState.emailDirection,
			inclusionEnabled: modalState.inclusionEnabled,
			domainRules: modalState.domainRules
				.map((rule) => mapEmailRuleToState(rule))
				.sort((a, b) => stringSort(a.value, b.value)),
			senderTypeRules: modalState.senderTypeRules
				.map((rule) => mapEmailRuleToState(rule))
				.sort((a, b) => stringSort(a.value, b.value)),
			specificEmailRules: modalState.specificEmailRules
				.map((rule) => mapEmailRuleToState(rule))
				.sort((a, b) => stringSort(a.value, b.value)),
		});
	};

	const hasModalStateChanged = () => {
		const gmailState = {
			enabled: gmailEnabled,
			syncStart: gmailSyncStart,
			label: '',
		};
		if (hasGoogleProvider) {
			gmailState.label = selectedGmailLabel || '';
		}
		const microsoftState = {
			enabled: microsoftEnabled,
			syncStart: microsoftSyncStart,
			folder: '',
		};
		if (hasMicrosoftProvider) {
			microsoftState.folder = selectedMicrosoftFolder || '';
		}
		const newState = {
			gmail: gmailState,
			microsoft: microsoftState,
			includeEmailBody: emailBodySync,
			syncAttachments: syncAttachments,
			emailDirection: emailDirection,
			inclusionEnabled: inclusionEnabled,
			domainRules: domainRules,
			senderTypeRules: senderTypeRules,
			specificEmailRules: specificEmailRules,
		};
		const newStateString = sortSimplifyStringifyModalState(newState);
		return (
			initialModalStateString !== '' &&
			initialModalStateString !== newStateString
		);
	};

	// Provider sync configurations
	const [microsoftSyncStart, setMicrosoftSyncStart] = useState(null);
	const [microsoftSyncStartConfigValue, setMicrosoftSyncStartConfigValue] =
		useState(null);
	const [gmailSyncStart, setGmailSyncStart] = useState(null);
	const [gmailSyncStartConfigValue, setGmailSyncStartConfigValue] =
		useState(null);

	const [microsoftEnabled, setMicrosoftEnabled] = useState(false);
	const [selectedMicrosoftFolder, setSelectedMicrosoftFolder] = useState('');
	const [microsoftFolders, setMicrosoftFolders] = useState([]);
	const [microsoftFolderLoading, setMicrosoftFolderLoading] = useState(false);
	const [microsoftFolderError, setMicrosoftFolderError] = useState(null);

	const [gmailEnabled, setGmailEnabled] = useState(false);
	const [selectedGmailLabel, setSelectedGmailLabel] = useState('');
	const [gmailLabels, setGmailLabels] = useState([]);
	const [gmailLabelLoading, setGmailLabelLoading] = useState(false);
	const [gmailLabelError, setGmailLabelError] = useState(null);

	// Controlled form state for the modal
	const [criteriaType, setCriteriaType] = useState('domain');
	const [criteriaValue, setCriteriaValue] = useState('');

	// Email rules
	const [domainRules, setDomainRules] = useState([]);
	const [senderTypeRules, setSenderTypeRules] = useState([]);
	const [specificEmailRules, setSpecificEmailRules] = useState([]);

	const theme = useTheme();

	// 'Include Email Body in CRM Sync' Toggle
	const initialEmailBodySync = tenant?.email_body_sync && user?.email_body_sync;

	const [emailBodySync, setEmailBodySync] = useState(initialEmailBodySync);

	// 'Email Ingestion Direction' Dropdown
	const [emailDirection, setEmailDirection] = useState(tenant?.email_direction);

	const handleRuleTypeChange = async (event) => {
		const newInclusionEnabled = event.target.checked;

		setConfigLoading(true);
		setInclusionEnabled(newInclusionEnabled);

		try {
			const newEmailRules = Object.groupBy(
				await fetchInitialEmailRules(newInclusionEnabled),
				(rule) => rule.criteria_type
			);

			setDomainRules(newEmailRules.domain || []);
			setSenderTypeRules(newEmailRules.sender_type || []);
			setSpecificEmailRules(newEmailRules.specific_email || []);
		} finally {
			setConfigLoading(false);
		}
	};

	const handleMicrosoftAutoSyncChange = (event) => {
		const newValue = event.target.checked;
		setMicrosoftEnabled(newValue);
		if (!newValue) {
			setMicrosoftSyncStart(null);
			setMicrosoftSyncStartConfigValue(null);
		}
	};

	const handleGmailAutoSyncChange = (event) => {
		const newValue = event.target.checked;
		setGmailEnabled(newValue);
		if (!newValue) {
			setGmailSyncStart(null);
			setGmailSyncStartConfigValue(null);
		}
	};

	const handleAddEmailRule = () => {
		const trimmedValue = criteriaValue.trim();

		// Prevent empty or whitespace-only rules from being added
		if (!trimmedValue) {
			return;
		}

		if (criteriaType === 'domain') {
			setDomainRules([
				...domainRules,
				{
					rule_type: inclusionEnabled ? 'inclusion' : 'exclusion',
					value: trimmedValue,
					criteria_type: criteriaType,
					user_id: user.id,
				},
			]);
		}
		if (criteriaType === 'sender_type') {
			setSenderTypeRules([
				...senderTypeRules,
				{
					rule_type: inclusionEnabled ? 'inclusion' : 'exclusion',
					value: trimmedValue,
					criteria_type: criteriaType,
					user_id: user.id,
				},
			]);
		}
		if (criteriaType === 'specific_email') {
			setSpecificEmailRules([
				...specificEmailRules,
				{
					rule_type: inclusionEnabled ? 'inclusion' : 'exclusion',
					value: trimmedValue,
					criteria_type: criteriaType,
					user_id: user.id,
				},
			]);
		}

		setCriteriaValue(''); // Clear form on success
	};

	const handleDeleteEmailRule = (type, id) => {
		if (type === 'domain') {
			setDomainRules(domainRules.filter((rule) => rule.id !== id));
		}
		if (type === 'sender_type') {
			setSenderTypeRules(senderTypeRules.filter((rule) => rule.id !== id));
		}
		if (type === 'specific_email') {
			setSpecificEmailRules(
				specificEmailRules.filter((rule) => rule.id !== id)
			);
		}
	};

	// Handle Microsoft folder selection - just update state, don't save yet
	const handleMicrosoftFolderChange = (folderId) => {
		setSelectedMicrosoftFolder(folderId);
	};

	// Fetch Microsoft folders when Microsoft is enabled
	const handleFetchMicrosoftFolders = async (configId) => {
		setMicrosoftFolderLoading(true);
		setMicrosoftFolderError(null);
		try {
			const result = await fetchMicrosoftFolders(configId);
			if (result.success) {
				const fetchedFolders = result.data.folders || [];
				setMicrosoftFolders(fetchedFolders);
				return fetchedFolders;
			} else {
				setMicrosoftFolderError('Failed to load Microsoft folders');
				return null;
			}
		} catch (error) {
			setMicrosoftFolderError('Error loading Microsoft folders');
			return null;
		} finally {
			setMicrosoftFolderLoading(false);
		}
	};

	// Handle Gmail label selection - just update state, don't save yet
	const handleGmailLabelChange = (labelId) => {
		setSelectedGmailLabel(labelId);
	};

	// Fetch Gmail labels when Gmail is enabled
	const handleFetchGmailLabels = async (configId) => {
		setGmailLabelLoading(true);
		setGmailLabelError(null);
		try {
			const result = await fetchGmailLabels(configId);
			if (result.success) {
				const labels = result.data.labels || [];
				setGmailLabels(labels);
				return labels;
			} else {
				setGmailLabelError('Failed to load Gmail labels');
				return null;
			}
		} catch (error) {
			setGmailLabelError('Error loading Gmail labels');
			return null;
		} finally {
			setGmailLabelLoading(false);
		}
	};

	const microsoftInputRef = useRef(null);
	const originalMicrosoftFolderRef = useRef('');
	const gmailInputRef = useRef(null);
	const originalGmailLabelRef = useRef('');

	const handleClose = () => {
		const isModalStateInitialized =
			initialModalState &&
			initialModalState.microsoft &&
			initialModalState.gmail &&
			![null, undefined].includes(initialModalState.inclusionEnabled) &&
			![null, undefined].includes(initialModalState.includeEmailBody) &&
			![null, undefined].includes(initialModalState.syncAttachments) &&
			![null, undefined].includes(initialModalState.emailDirection);

		// Discard unsaved changes by restoring the state captured when the modal opened.
		if (isModalStateInitialized && hasModalStateChanged()) {
			setMicrosoftEnabled(initialModalState.microsoft.enabled);
			setMicrosoftSyncStart(initialModalState.microsoft.syncStart);
			setSelectedMicrosoftFolder(initialModalState.microsoft.folder);

			setGmailEnabled(initialModalState.gmail.enabled);
			setGmailSyncStart(initialModalState.gmail.syncStart);
			setSelectedGmailLabel(initialModalState.gmail.label);

			setInclusionEnabled(initialModalState.inclusionEnabled);
			setSyncAttachments(initialModalState.syncAttachments);
			setEmailBodySync(initialModalState.includeEmailBody);
			setEmailDirection(initialModalState.emailDirection);

			setDomainRules(initialModalState.domainRules);
			setSenderTypeRules(initialModalState.senderTypeRules);
			setSpecificEmailRules(initialModalState.specificEmailRules);
		}

		// Clear the snapshot so the next modal session starts with fresh values.
		initialModalStateString = '';
		initialModalState = {};

		onClose();
	};

	const handleSave = async () => {
		const selectedLabelObj = gmailLabels.find(
			(label) => label.id === selectedGmailLabel
		);

		const gmailState = {
			enabled: gmailEnabled,
			sync_start_at: gmailSyncStart,
		};

		if (hasGoogleProvider) {
			gmailState.container_id = selectedGmailLabel || null;
			gmailState.container_name = selectedLabelObj?.name || null;
		}

		const selectedFolderObj = microsoftFolders.find(
			(folder) => folder.id === selectedMicrosoftFolder
		);

		const microsoftState = {
			enabled: microsoftEnabled,
			sync_start_at: microsoftSyncStart,
		};

		if (hasMicrosoftProvider) {
			microsoftState.container_id = selectedMicrosoftFolder || null;
			microsoftState.container_name = selectedFolderObj?.name || null;
		}

		const newState = {
			...(hasGoogleProvider && { gmail: gmailState }),
			...(hasMicrosoftProvider && { microsoft: microsoftState }),
			include_email_body: emailBodySync,
			email_direction: emailDirection,
			inclusion_enabled: inclusionEnabled,
			sync_attachments: syncAttachments,
			domain_rules: domainRules,
			sender_type_rules: senderTypeRules,
			specific_email_rules: specificEmailRules,
			report_id: (reportId || '').trim(),
		};

		if (hasModalStateChanged()) {
			const message = {
				successMessage: 'Successfully updated!',
				errorMessage: 'Failed to update.',
				errorLog:
					'Failed to update email sync config settings due to the error:',
			};

			try {
				await authedApiFetch({
					endpoint: `/tenants/${tenant.id}/email_sync_configuration_settings`,
					method: 'POST',
					payload: {
						tenant_level: false,
						new_state: newState,
					},
				});

				tenant.use_inclusion_email_rules = inclusionEnabled;
				user.email_body_sync = emailBodySync;

				setInclusionEnabled(inclusionEnabled);
				setSyncAttachments(syncAttachments);
				setEmailBodySync(emailBodySync);
				setEmailDirection(emailDirection);
				onConfigChange();
				showNotification(message.successMessage, 'success');
			} catch (error) {
				handleClose();
				console.error(message.errorLog, error.message);
				showNotification(message.errorMessage, 'error');
				return;
			}
		}

		// Clear the initial state so the next time the modal opens,
		// change tracking starts with freshly loaded values
		initialModalStateString = '';
		initialModalState = {};
		onClose();
	};

	// Dynamic Domain List (Report-driven sync)
	const [emailSyncConfig, setEmailSyncConfig] = useState(null); // null => not configured
	const [reportId, setReportId] = useState('');
	const [configLoading, setConfigLoading] = useState(false);
	const [runningEmailDomainSync, setRunningEmailDomainSync] = useState(false);
	const isRunning =
		emailSyncConfig?.status === 'running' || isMostRecentJobBlocking;
	const hasConfig = !!emailSyncConfig;
	const shouldDisableGmailDatePicker =
		gmailEnabled && gmailSyncStartConfigValue;
	const shouldDisableMicrosoftDatePicker =
		microsoftEnabled && microsoftSyncStartConfigValue;

	const fetchEmailDomainSyncConfig = async () => {
		let cfg;
		try {
			setConfigLoading(true);
			cfg = await authedApiFetch({
				endpoint: `/tenants/${tenant.id}/email_domain_sync`,
				method: 'GET',
			});
			setEmailSyncConfig(cfg);
			setReportId(cfg.report_id || '');
		} catch (e) {
			setEmailSyncConfig(null);
			setReportId('');
			return '';
		} finally {
			setConfigLoading(false);
		}
		return cfg;
	};

	// Fetch latest status from backend; returns updated config or null if 404
	const fetchEmailDomainSyncStatus = async () => {
		try {
			const cfg = await authedApiFetch({
				endpoint: `/tenants/${tenant.id}/email_domain_sync`,
				method: 'GET',
			});
			setEmailSyncConfig(cfg);
			return cfg;
		} catch (e) {
			if (e?.status === 404) {
				setEmailSyncConfig(null);
			}
			return null;
		}
	};

	useEffect(() => {
		if (!open || !tenant?.id || !user?.id) return;

		const fetchData = async () => {
			setConfigLoading(true);

			try {
				const fetchedEmailDomainSyncConfig = await fetchEmailDomainSyncConfig();

				const fetchedReportId = fetchedEmailDomainSyncConfig?.report_id || '';

				const initialEmailRules = Object.groupBy(
					await fetchInitialEmailRules(inclusionEnabled),
					(rule) => rule.criteria_type
				);

				const initialDomainRules = initialEmailRules.domain || [];
				const initialSenderRules = initialEmailRules.sender_type || [];
				const initialSpecificEmailRules =
					initialEmailRules.specific_email || [];

				// User setting is enabled only when both tenant and user allow it.
				const shouldIncludeEmailBody =
					tenant?.email_body_sync && user?.email_body_sync;

				const initialEmailDirection = tenant?.email_direction;
				const initialSyncAttachments = tenant?.sync_attachments;

				setDomainRules(initialDomainRules);
				setSenderTypeRules(initialSenderRules);
				setSpecificEmailRules(initialSpecificEmailRules);

				setEmailBodySync(shouldIncludeEmailBody);
				setEmailDirection(initialEmailDirection);
				setSyncAttachments(initialSyncAttachments);

				setRunningEmailDomainSync(
					fetchedEmailDomainSyncConfig?.status === 'running'
				);

				let initialMicrosoftEnabled = false;
				let initialMicrosoftSyncStart = null;
				let initialMicrosoftFolder = '';

				let initialGoogleEnabled = false;
				let initialGoogleSyncStart = null;
				let initialGoogleLabel = '';

				const result = await fetchEmailSyncConfigurations();

				if (result.success && result.data) {
					let microsoftConfig = result.data.find(
						(config) => config.provider === 'microsoft'
					);

					let gmailConfig = result.data.find(
						(config) => config.provider === 'google'
					);

					if (!microsoftConfig) {
						const ensureResult = await ensureMicrosoftConfigExists();

						if (ensureResult.success) {
							microsoftConfig = {
								id: ensureResult.configId,
							};
						}
					}

					if (!gmailConfig) {
						const ensureResult = await ensureGmailConfigExists();

						if (ensureResult.success) {
							gmailConfig = {
								id: ensureResult.configId,
							};
						}
					}

					if (microsoftConfig) {
						initialMicrosoftEnabled = microsoftConfig.enabled || false;

						setMicrosoftEnabled(initialMicrosoftEnabled);

						if (microsoftConfig.sync_start_at) {
							const parsedDate = dayjs(microsoftConfig.sync_start_at);

							if (parsedDate.isValid()) {
								initialMicrosoftSyncStart = parsedDate;
								setMicrosoftSyncStart(parsedDate);
								setMicrosoftSyncStartConfigValue(parsedDate);
							}
						}

						if (microsoftConfig.source_container_id) {
							initialMicrosoftFolder = microsoftConfig.source_container_id;

							setSelectedMicrosoftFolder(initialMicrosoftFolder);

							originalMicrosoftFolderRef.current = initialMicrosoftFolder;
						}

						if (hasMicrosoftProvider) {
							await handleFetchMicrosoftFolders(microsoftConfig.id);
						}
					}

					if (gmailConfig) {
						initialGoogleEnabled = gmailConfig.enabled || false;

						setGmailEnabled(initialGoogleEnabled);

						if (gmailConfig.sync_start_at) {
							const parsedDate = dayjs(gmailConfig.sync_start_at);

							if (parsedDate.isValid()) {
								initialGoogleSyncStart = parsedDate;
								setGmailSyncStart(parsedDate);
								setGmailSyncStartConfigValue(parsedDate);
							}
						}

						if (gmailConfig.source_container_id) {
							initialGoogleLabel = gmailConfig.source_container_id;

							setSelectedGmailLabel(initialGoogleLabel);

							originalGmailLabelRef.current = initialGoogleLabel;
						}

						if (hasGoogleProvider) {
							await handleFetchGmailLabels(gmailConfig.id);
						}
					}
				}

				const initialMicrosoftState = {
					enabled: initialMicrosoftEnabled,
					syncStart: initialMicrosoftSyncStart,
					folder: hasMicrosoftProvider ? initialMicrosoftFolder : '',
				};

				const initialGmailState = {
					enabled: initialGoogleEnabled,
					syncStart: initialGoogleSyncStart,
					label: hasGoogleProvider ? initialGoogleLabel : '',
				};

				// Store initial values to detect and reset unsaved changes.
				initialModalState = {
					gmail: initialGmailState,
					microsoft: initialMicrosoftState,
					includeEmailBody: shouldIncludeEmailBody,
					syncAttachments: initialSyncAttachments,
					emailDirection: initialEmailDirection,
					inclusionEnabled: tenant.use_inclusion_email_rules,
					domainRules: initialDomainRules,
					senderTypeRules: initialSenderRules,
					specificEmailRules: initialSpecificEmailRules,
					salesforceReportId: fetchedReportId.trim(),
				};

				initialModalStateString =
					sortSimplifyStringifyModalState(initialModalState);
			} finally {
				setConfigLoading(false);
			}
		};

		fetchData();
	}, [open, tenant?.id, user?.id]);

	// Poll while the Dynamic Domain List is syncing
	useEffect(() => {
		if (!open || !tenant?.id || emailSyncConfig?.status !== 'running') {
			return;
		}

		const interval = setInterval(async () => {
			const config = await fetchEmailDomainSyncStatus();

			if (config?.status === 'running') {
				setRunningEmailDomainSync(true);
				return;
			}

			clearInterval(interval);
			setRunningEmailDomainSync(false);

			// Refresh domain rules after the dynamic domain sync completes
			if (config?.status === 'succeeded') {
				fetchEmailRules();
			}
		}, 2000);

		return () => clearInterval(interval);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, tenant?.id, emailSyncConfig?.status]);

	// If a config exists, do not allow selecting 'domain' for manual rule creation
	useEffect(() => {
		if (hasConfig && criteriaType === 'domain') {
			setCriteriaType('sender_type');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasConfig]);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth='md'
			fullWidth
			scroll='paper'
		>
			<DialogTitle>Email Sync Configuration</DialogTitle>
			<DialogContent dividers>
				{hasModalStateChanged() && (
					<Alert
						severity='info'
						sx={{
							mb: 2,
							backgroundColor: theme.palette.background.lightBlue,
						}}
					>
						There are unsaved changes to your Email Sync Configuration, to save
						them, please press the Save button.
					</Alert>
				)}
				<AutomaticEmailSync
					hasMicrosoftProvider={hasMicrosoftProvider}
					hasGoogleProvider={hasGoogleProvider}
					microsoftEnabled={microsoftEnabled}
					onMicrosoftEnabledChange={handleMicrosoftAutoSyncChange}
					microsoftSyncStart={microsoftSyncStart}
					onMicrosoftSyncStartChange={setMicrosoftSyncStart}
					microsoftSyncStartMin={microsoftSyncStartMin}
					microsoftInputRef={microsoftInputRef}
					shouldDisableMicrosoftDatePicker={shouldDisableMicrosoftDatePicker}
					gmailEnabled={gmailEnabled}
					onGmailEnabledChange={handleGmailAutoSyncChange}
					gmailSyncStart={gmailSyncStart}
					onGmailSyncStartChange={setGmailSyncStart}
					googleSyncStartMin={googleSyncStartMin}
					gmailInputRef={gmailInputRef}
					shouldDisableGmailDatePicker={shouldDisableGmailDatePicker}
					isRunning={isRunning}
				/>
				<Divider sx={{ my: 2 }} />

				<BasicSettings
					tenant={tenant}
					tenantLevel={false}
					emailBodySync={emailBodySync}
					setEmailBodySync={setEmailBodySync}
					syncAttachments={syncAttachments}
					setSyncAttachments={setSyncAttachments}
					emailDirection={emailDirection}
					setEmailDirection={setEmailDirection}
					hasMicrosoftProvider={hasMicrosoftProvider}
					microsoftFolders={microsoftFolders}
					selectedMicrosoftFolder={selectedMicrosoftFolder}
					handleMicrosoftFolderChange={handleMicrosoftFolderChange}
					microsoftFolderLoading={microsoftFolderLoading}
					microsoftFolderError={microsoftFolderError}
					hasGoogleProvider={hasGoogleProvider}
					gmailLabels={gmailLabels}
					selectedGmailLabel={selectedGmailLabel}
					handleGmailLabelChange={handleGmailLabelChange}
					gmailLabelLoading={gmailLabelLoading}
					gmailLabelError={gmailLabelError}
					isRunning={isRunning}
				/>

				<Divider sx={{ my: 3 }} />

				<EmailRules
					tenantLevel={false}
					inclusionEnabled={inclusionEnabled}
					handleRuleTypeChange={handleRuleTypeChange}
					isRunning={isRunning}
					criteriaType={criteriaType}
					setCriteriaType={setCriteriaType}
					criteriaValue={criteriaValue}
					setCriteriaValue={setCriteriaValue}
					handleAddEmailRule={handleAddEmailRule}
					handleDeleteEmailRule={handleDeleteEmailRule}
					domainRules={domainRules}
					senderTypeRules={senderTypeRules}
					specificEmailRules={specificEmailRules}
					hasConfig={hasConfig}
					reportId={reportId}
				/>

				<Divider sx={{ mt: 4, mb: 3 }} />

				<DynamicDomainList
					tenantLevel={false}
					reportId={reportId}
					configLoading={configLoading}
					runningEmailDomainSync={runningEmailDomainSync}
					emailSyncConfig={emailSyncConfig}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} variant='contained'>
					Cancel
				</Button>
				<Button onClick={handleSave} variant='contained'>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default UserEmailSyncConfigModal;
